import { useCallback, useEffect, useState } from "react";
import { Briefcase, BarChart3, Layers, Loader2, Plus, X, Trash2, UserPlus, UserMinus } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { EmployeePicker } from "../../components/os/EmployeePicker";
import { api } from "../../lib/api";

const STATE_TONE = {
  ocupada: "bg-emerald-500/15 text-emerald-600", vacante: "bg-gold/15 text-gold",
  autorizada: "bg-sky-500/15 text-sky-600", congelada: "bg-navy/10 text-navy/50",
  en_proceso: "bg-violet/15 text-violet",
};
const money = (n) => `$${Number(n || 0).toLocaleString("es-MX")}`;

function Modal({ title, onClose, children, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testid}>
      <div className={`bg-white rounded-2xl w-full ${wide ? "max-w-lg" : "max-w-md"} p-6 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-2xl">{title}</h3><button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button></div>
        {children}
      </div>
    </div>
  );
}

export default function Positions({ embedded = false }) {
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("positions");
  useEffect(() => { api.get("/positions/meta").then((r) => setMeta(r.data)).catch(() => setMeta({ is_leader: false, states: [], state_labels: {}, areas: [] })); }, []);
  const body = (
      <div className="space-y-6" data-testid="positions-page">
        <div className="flex gap-2 border-b border-navy/10 pb-2">
          {[["positions", "Puestos", Briefcase], ["headcount", "Headcount", BarChart3], ["arch", "Job Architecture", Layers]].map(([k, l, Ic]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`pos-tab-${k}`} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${tab === k ? "bg-navy text-cream" : "text-navy/55 hover:bg-navy/5"}`}><Ic className="w-4 h-4" /> {l}</button>
          ))}
        </div>
        {!meta ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <>
            {tab === "positions" && <PositionsList meta={meta} />}
            {tab === "headcount" && <Headcount />}
            {tab === "arch" && <Architecture />}
          </>
        )}
      </div>
  );
  if (embedded) return body;
  return <OSLayout title="OMNIA Puestos y Plantilla">{body}</OSLayout>;
}

function PositionsList({ meta }) {
  const [items, setItems] = useState([]);
  const [area, setArea] = useState("");
  const [estado, setEstado] = useState("");
  const [open, setOpen] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const load = useCallback(() => {
    const p = new URLSearchParams(); if (area) p.set("area", area); if (estado) p.set("estado", estado);
    api.get(`/positions?${p.toString()}`).then((r) => setItems(r.data || [])).catch(() => {});
  }, [area, estado]);
  useEffect(() => { load(); }, [load]);
  const setState = async (id, e) => { try { await api.patch(`/positions/${id}`, { estado: e }); load(); } catch { toast.error("Error"); } };
  const del = async (id) => { try { await api.delete(`/positions/${id}`); load(); } catch { toast.error("Error"); } };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select className="field w-auto" value={area} onChange={(e) => setArea(e.target.value)} data-testid="pos-filter-area"><option value="">Todas las áreas</option>{meta.areas.map((a) => <option key={a} value={a}>{a}</option>)}</select>
        <select className="field w-auto" value={estado} onChange={(e) => setEstado(e.target.value)} data-testid="pos-filter-estado"><option value="">Todos los estados</option>{meta.states.filter((s) => s !== "eliminada").map((s) => <option key={s} value={s}>{meta.state_labels[s]}</option>)}</select>
        {meta.is_leader && <button onClick={() => setOpen(true)} className="btn btn-primary gap-2 ml-auto" data-testid="pos-new"><Plus className="w-4 h-4" /> Nuevo puesto</button>}
      </div>
      {items.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">Sin puestos.</div>
        : <div className="grid gap-3 sm:grid-cols-2">{items.map((p) => (
          <div key={p.id} className="bg-white border border-navy/10 rounded-2xl p-4" data-testid={`pos-${p.id}`}>
            <div className="flex items-start justify-between">
              <div><div className="text-sm font-medium">{p.titulo}</div><div className="text-xs text-navy/50">{[p.area, p.departamento].filter(Boolean).join(" · ") || "—"} · Nivel {p.nivel_jerarquico}</div></div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${STATE_TONE[p.estado] || "bg-navy/10"}`}>{meta.state_labels[p.estado]}</span>
            </div>
            <div className="text-xs text-navy/40 mt-2">{money(p.salario_min)}{p.salario_max > p.salario_min ? `–${money(p.salario_max)}` : ""} {p.moneda}{p.ocupante_name && ` · ${p.ocupante_name}`}</div>
            {meta.is_leader && <div className="flex flex-wrap gap-2 mt-3">
              <button onClick={() => setAssignFor(p)} className="btn btn-ghost text-xs gap-1" data-testid={`pos-assign-${p.id}`}>{p.ocupante_id ? <><UserMinus className="w-3.5 h-3.5" /> Reasignar</> : <><UserPlus className="w-3.5 h-3.5" /> Asignar</>}</button>
              {p.estado === "vacante" && <button onClick={() => setState(p.id, "congelada")} className="btn btn-ghost text-xs">Congelar</button>}
              {p.estado === "congelada" && <button onClick={() => setState(p.id, "vacante")} className="btn btn-ghost text-xs">Reactivar</button>}
              <button onClick={() => del(p.id)} className="text-navy/30 hover:text-destructive ml-auto"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>}
          </div>
        ))}</div>}
      {open && <PositionDialog meta={meta} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {assignFor && <AssignDialog pos={assignFor} onClose={() => setAssignFor(null)} onDone={() => { setAssignFor(null); load(); }} />}
    </div>
  );
}

function PositionDialog({ meta, onClose, onSaved }) {
  const [f, setF] = useState({ titulo: "", area: "", departamento: "", nivel_jerarquico: 5, tipo_contrato: "planta", salario_min: 0, salario_max: 0, estado: "vacante", descripcion: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.titulo.trim()) return toast.error("Título requerido");
    setBusy(true);
    try { await api.post("/positions", { ...f, nivel_jerarquico: Number(f.nivel_jerarquico), salario_min: Number(f.salario_min), salario_max: Number(f.salario_max) }); toast.success("Puesto creado"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Nuevo puesto" onClose={onClose} testid="pos-dialog" wide>
      <div className="space-y-3">
        <input className="field" placeholder="Título del puesto" value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} data-testid="pos-titulo" />
        <div className="grid grid-cols-2 gap-3">
          <input className="field" placeholder="Área" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} data-testid="pos-area" />
          <input className="field" placeholder="Departamento" value={f.departamento} onChange={(e) => setF({ ...f, departamento: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-navy/50">Nivel jerárquico</label><input type="number" className="field" value={f.nivel_jerarquico} onChange={(e) => setF({ ...f, nivel_jerarquico: e.target.value })} /></div>
          <div><label className="text-xs text-navy/50">Tipo de contrato</label><select className="field" value={f.tipo_contrato} onChange={(e) => setF({ ...f, tipo_contrato: e.target.value })}><option value="planta">Planta</option><option value="temporal">Temporal</option><option value="honorarios">Honorarios</option><option value="practicante">Practicante</option></select></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-navy/50">Salario mín. (mensual)</label><input type="number" className="field" value={f.salario_min} onChange={(e) => setF({ ...f, salario_min: e.target.value })} data-testid="pos-salario-min" /></div>
          <div><label className="text-xs text-navy/50">Salario máx.</label><input type="number" className="field" value={f.salario_max} onChange={(e) => setF({ ...f, salario_max: e.target.value })} /></div>
        </div>
        <select className="field" value={f.estado} onChange={(e) => setF({ ...f, estado: e.target.value })} data-testid="pos-estado">{meta.states.filter((s) => s !== "eliminada").map((s) => <option key={s} value={s}>{meta.state_labels[s]}</option>)}</select>
      </div>
      <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className="btn btn-ghost">Cancelar</button><button onClick={save} disabled={busy} className="btn btn-primary" data-testid="pos-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear"}</button></div>
    </Modal>
  );
}

function AssignDialog({ pos, onClose, onDone }) {
  const [ocupante, setOcupante] = useState(pos.ocupante_id || "");
  const [busy, setBusy] = useState(false);
  const save = async (clear) => {
    setBusy(true);
    try { await api.post(`/positions/${pos.id}/assign`, { ocupante_id: clear ? null : ocupante }); toast.success(clear ? "Puesto liberado" : "Colaborador asignado"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Asignar · ${pos.titulo}`} onClose={onClose} testid="pos-assign-dialog">
      <div className="space-y-3">
        <label className="text-xs font-medium text-navy/60 block">Colaborador</label>
        <EmployeePicker value={ocupante} onChange={(e) => setOcupante(e?.id || "")} testid="pos-assign-picker" />
      </div>
      <div className="flex justify-between gap-2 mt-5">
        {pos.ocupante_id ? <button onClick={() => save(true)} disabled={busy} className="btn btn-ghost text-destructive">Liberar puesto</button> : <span />}
        <button onClick={() => save(false)} disabled={busy || !ocupante} className="btn btn-primary" data-testid="pos-assign-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Asignar"}</button>
      </div>
    </Modal>
  );
}

function Headcount() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/positions/headcount").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const cards = [["Plantilla real", d.headcount_real], ["Posiciones", d.total_posiciones], ["Ocupadas", d.ocupadas], ["Vacantes", d.vacantes], ["Autorizadas", d.autorizadas], ["Costo mensual", money(d.costo_mensual)]];
  return (
    <div className="space-y-4" data-testid="pos-headcount">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map(([l, v]) => (<div key={l} className="bg-white border border-navy/10 rounded-2xl p-4"><div className="text-xs text-navy/40">{l}</div><div className="font-editorial text-2xl mt-1">{v}</div></div>))}
      </div>
      <div className="bg-white border border-navy/10 rounded-2xl p-4">
        <div className="text-sm font-medium mb-3">Por área</div>
        {d.by_area.length === 0 ? <div className="text-sm text-navy/40">Sin posiciones registradas.</div>
          : <div className="space-y-2">{d.by_area.map((a) => (
            <div key={a.area} className="flex items-center justify-between text-sm border-b border-navy/5 pb-2">
              <span className="font-medium">{a.area}</span>
              <span className="text-navy/50 text-xs">{a.total} posiciones · {a.ocupadas} ocupadas · {a.vacantes} vacantes</span>
            </div>
          ))}</div>}
      </div>
    </div>
  );
}

function Architecture() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/positions/job-architecture").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  return (
    <div className="space-y-4" data-testid="pos-arch">
      {d.levels.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">Sin arquitectura de puestos aún. Crea puestos en la pestaña Puestos.</div>
        : d.levels.map((lv) => (
          <div key={lv.nivel} className="bg-white border border-navy/10 rounded-2xl p-4">
            <div className="text-sm font-medium mb-3">Nivel jerárquico {lv.nivel} <span className="text-xs text-navy/40">· {lv.positions.length} puestos</span></div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{lv.positions.map((p) => (
              <div key={p.id} className="border border-navy/10 rounded-xl p-3">
                <div className="text-sm font-medium">{p.titulo}</div>
                <div className="text-xs text-navy/50">{p.area || "—"} · {money(p.salario_min)} {p.moneda}</div>
              </div>
            ))}</div>
          </div>
        ))}
    </div>
  );
}
